
import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { IonNav } from '@ionic/angular/standalone';
import { Flow, FlowDirector, FlowPage, FlowPageOptions } from '../flow-director';
import { YshSplitLayoutComponent } from '../../components/ysh-split-layout/ysh-split-layout.component';
import { TimeSelectorPage } from './time-selector/time-selector.page';
import { AppointmentBuilderPage } from './appointment-builder/appointment-builder.page';
import { ServiceGroup } from '../../models/service-group';
import { Service } from '../../models/service';
import { TimeSlot } from '../../models/time-slot';
import { MultipleOrderParams } from '../../services/api/time-slot/time-slot.service';
import { ServiceService } from '../../services/api/service/service.service';
import { BackButtonService } from '../../shared/back-button.service';
import { OrderCreateBulkParams, OrderService } from '../../services/api/order/order.services';
import { User } from '../../models/user';
import { combineLatest, filter, from, map, mergeMap, of, Subject, take, takeUntil, tap } from 'rxjs';
import { Order } from '../../models/order';
import { OrderCompletePage } from './order-complete/order-complete.page';
import { Router } from '@angular/router';
import { AppRoutes, companyRoute } from '../../app.routes';
import { ServiceSelectorPage } from './service-selector/service-selector.page';
import { UserService } from '../../services/api/user/user.service';
import { ToastService } from '../../services/ui/toast/toast.service';
import { AddCardPage } from '../add-card/add-card.page';
import { addCardCopy } from './add-card-copy';
import { CardHolder, CardRequirement, PaymentPlan } from './payment-plan';
import { ModalService } from '../../services/ui/modal/modal.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { Company } from '../../models/company';
import { CompanyService } from '../../services/api/company/company.service';
import { Action, PermissionService, Resource, ResourcePermissions } from '../../services/permission/permission.service';
import { Role } from '../../models/affiliation';

export interface OrderFlowProps {
  serviceGroup: ServiceGroup;
}

interface OrderFlowForm {
  multipleOrderParams?: Nullable<MultipleOrderParams[]>;
  company?: Company;
  canAddCompanyPaymentMethod?: boolean;
  duration?: string;
  availableSlots?: TimeSlot[];
  serviceGroup?: ServiceGroup;
  timeSlot?: TimeSlot;
  user?: User;
  orders?: Order[];
  services?: Service[];
  didCompleteAppointmentBuilder?: boolean;
  didComplete?: boolean;
}

@Component({
    selector: 'ysh-ordering-flow',
    template: `
    <ysh-split-layout 
      [theme]="isOrderCompletePage ? 'blue' : 'smoke'"
      [hideVisualForMobile]="true"
      [reverseImage]="!isOrderCompletePage"
      [containImage]="isOrderCompletePage"
      [srcImage]="isOrderCompletePage && '/assets/images/graphic-order-complete.svg'"
    >
      <ion-nav #webflowNav></ion-nav>
    </ysh-split-layout>`,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        YshSplitLayoutComponent, IonNav
    ]
})
export class OrderingFlow implements Flow, OnInit, OnDestroy {
  @ViewChild('webflowNav') nav: IonNav;
  @Input() props: OrderFlowProps;
  @Input() companyUid?: string;
  @Input() onDismiss: () => void;

  form: OrderFlowForm = {}

  // Flow
  flowDirector: FlowDirector;
  preventAnimation?: boolean = true;
  isOrderCompletePage: boolean = false;

  count = 1;
  viewerIsDriver = false;
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private analytics: AnalyticsService,
    private companyService: CompanyService,
    private modalService: ModalService,
    private permissionService: PermissionService,
    private router: Router,
    private serviceService: ServiceService, 
    private toastService: ToastService,
    private userService: UserService,
    public backButtonService: BackButtonService, 
    public orderService: OrderService) {}

  ngOnInit() {
    this.userService.currentUser$.pipe(takeUntil(this.unsubscribe)).subscribe(user => {
      this.form.user = user;
      this.updatePriceVisibility();
    });
    this.companyService.currentCompany$.pipe(takeUntil(this.unsubscribe)).subscribe(company => {
      this.form.company = company;
      this.updatePriceVisibility();
      this.loadCompanyPaymentPermission(company);
    });
    // Load the available services for the currently selected company once both
    // the user and the selected company have resolved.
    combineLatest([this.userService.currentUser$, this.companyService.currentCompany$])
      .pipe(
        filter(([user]) => !!user),
        take(1),
        takeUntil(this.unsubscribe),
      )
      .subscribe(([, company]) => this.getServices(company));
    if (this.companyUid) {
      this.companyService.get(this.companyUid).pipe(
        tap((company) => {
          this.form.company = company;
          this.updatePriceVisibility();
        }),
        takeUntil(this.unsubscribe),
      ).subscribe((company) => this.loadCompanyPaymentPermission(company));
    }
  }

  private loadCompanyPaymentPermission(company: Nullable<Company>) {
    if (!company) {
      this.form.canAddCompanyPaymentMethod = false;
      return;
    }
    this.permissionService.getPermissions(company, [Resource.CreditCard]).pipe(
      map((permissions) => permissions.canAccess(Action.Post, Resource.CreditCard)),
      takeUntil(this.unsubscribe),
    ).subscribe((access) => this.form.canAddCompanyPaymentMethod = access);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }



  // flow

  nextPage(): Nullable<FlowPageOptions<FlowPage>> {
    if (!this.form.multipleOrderParams?.length) {
      return this.optionForServiceSelectorPage();
    }
    if (!this.form.didCompleteAppointmentBuilder){
      return this.optionsForAppointmentBuilder();
    }
    if (!this.form.timeSlot) {
      return this.optionsForTimeSelectorPage();
    }
    if (!this.form.didComplete){
      return this.optionsForOrderCompletePage();
    }
  }

  navigateToCompanyAppointments(): void {
    this.router.navigateByUrl(
      companyRoute(AppRoutes.Appointments, this.form.company)
    );
  }

  flowDidComplete(): void {
    this.navigateToCompanyAppointments();
  }

  // Page Params

  // ServiceSelectorPage

  private optionForServiceSelectorPage(): FlowPageOptions<ServiceSelectorPage> {
    return {
      page: ServiceSelectorPage,
      urlHash: 'select-service',
      onComplete: (service: Service) => {
        this.didSelectMultipleServices([service]);
      },
      onDismiss: () => this.navigateToCompanyAppointments(),
      props: {
        services: this.form.serviceGroup.services,
        payerLabel: this.payerLabel,
      },
    };
  }

  // AppointmentBuilderPage

  private optionsForAppointmentBuilder(): FlowPageOptions<AppointmentBuilderPage> {
    return {
      page: AppointmentBuilderPage,
      urlHash: 'build-appointment',
      onComplete: (services, slots) => {
        this.form.didCompleteAppointmentBuilder = true;
        this.didSelectMultipleServices(services, slots);
      },
      onDismiss: () => (this.form.didCompleteAppointmentBuilder = false),
      props: {
        services: this.form.services,
        serviceGroup: this.form.serviceGroup,
        payerLabel: this.payerLabel,
      },
    };
  }

  private didSelectMultipleServices(services: Service[], slots?: TimeSlot[]) {
    this.form.multipleOrderParams = services.map((item) => {
      return { serviceUid: item.uid };
    });
    const totalDuration: number = services.reduce((sum, service) => sum + service.stopDuration, 0);
    this.form.duration = Service.durationString(totalDuration);
    this.form.availableSlots = slots;
    this.form.services = services;
    this.flowDirector.next();
  }


  // TimeSelectorPage

  private optionsForTimeSelectorPage(): FlowPageOptions<TimeSelectorPage> {
    return {
      page: TimeSelectorPage,
      urlHash: 'select-time',
      onComplete: (slot: TimeSlot) => {
        this.form.timeSlot = slot;
        return from(this.promptForPayment()).pipe(
          mergeMap((ready) => ready ? this.saveOrder() : of(null)),
          map(Boolean)
        )
      },
      onDismiss: () => {
        this.form.timeSlot = null;
        this.onDismiss?.();
      },
      props: {
        serviceUids: this.form.multipleOrderParams.map(item => item.serviceUid),
        timeSlots: this.form.availableSlots,
        duration: this.form.duration
      },
    };
  }

  // OrderCompletePage

  private optionsForOrderCompletePage(): FlowPageOptions<OrderCompletePage> {
    this.isOrderCompletePage = true;
    return {
      page: OrderCompletePage,
      urlHash: 'order-complete',
      onComplete: () => {
        this.form.didComplete = true;
        this.flowDirector.next();
      },
      preventBackNavigation: true,
      props: {
        orders: this.form.orders,
        payerLabel: this.payerLabel,
      },
    };
  }

  // Pre-Order Modal Flow

  get paymentPlan(): PaymentPlan {
    return new PaymentPlan(
      this.form.services ?? [],
      this.form.user,
      this.form.company,
      this.form.canAddCompanyPaymentMethod,
    );
  }

  /** False if a required card was not provided. */
  private async promptForPayment(): Promise<boolean> {
    for (const requirement of this.paymentPlan.missingCards) {
      if (!(await this.presentAddCard(requirement))) {
        return false;
      }
    }
    return true;
  }

  private async presentAddCard(requirement: CardRequirement): Promise<boolean> {
    const plan = this.paymentPlan;
    let didAdd = false;

    // Every exit dismisses the sheet except a failed save, which leaves it open
    // for a retry, so dismissal is the signal and a refusal is simply no card.
    const paymentSheet = await this.modalService.present({
      component: AddCardPage,
      componentProps: {
        ...addCardCopy(requirement.reason, {
          companyName: this.form.company?.name,
          lateCancellationFee: plan.lateCancellationFee,
          noShowFee: plan.noShowFee,
        }),
        accountUid: this.accountUidFor(requirement),
        onComplete: (result: boolean) => {
          if (!result) return;
          this.didAddCard(requirement);
          didAdd = true;
        },
      },
    });
    await paymentSheet.onDidDismiss();

    return didAdd;
  }

  private accountUidFor(requirement: CardRequirement): Nullable<string> {
    return requirement.holder === CardHolder.Company ? this.form.company?.accountUid : undefined;
  }

  /** hasValidPaymentMethod comes from cached records. */
  private didAddCard(requirement: CardRequirement) {
    if (requirement.holder === CardHolder.Company && this.form.company) {
      this.form.company.hasValidPaymentMethod = true;
    } else if (this.form.user) {
      this.form.user.hasValidPaymentMethod = true;
    }
  }

  // Data

  getServices(company: Company | null) {
    const companyUid = this.companyUid || company?.uid;
    const search = 'DOT';
    this.serviceService.getMany({companyUid, search}).subscribe((response)=>{
      this.form.serviceGroup = ServiceGroup.groupServices(response.items)[0];
      this.flowDirector = new FlowDirector(this);
    });
  }

  /** A driver is not shown what their company is paying. */
  private updatePriceVisibility() {
    const selectedCompanyUid = this.form.company?.uid || this.companyUid;
    if (!this.form.user || !selectedCompanyUid) {
      this.viewerIsDriver = false;
      return;
    }
    const affiliation = this.form.user.affiliations?.find(
      (item) => item.company?.uid === selectedCompanyUid
    );
    this.viewerIsDriver = affiliation?.role === Role.Driver;
  }

  payerLabel = (service: Service): string => {
    if (this.viewerIsDriver && !service.billsRecipient) {
      return `Covered by ${this.form.company?.name || 'your company'}`;
    }
    return `$${service.price}`;
  };

  saveOrder() {
    const params: OrderCreateBulkParams = {
      slotStartDateTime: this.form.timeSlot.startTime,
      date: this.form.timeSlot.date,
      multipleOrderParams: this.form.multipleOrderParams,
      userUid: this.form.user.uid,
      userAddressUid: this.form.user.lastUserAddress?.uid,
      companyUid: this.companyUid
    };
    const req = this.orderService.createBulk(params)
    req.subscribe({
      next: (orders) => {
        this.form.orders = orders;
        this.analytics.trackPurchase(orders);
        this.flowDirector.next();
      },
      error: (error) => {
        const message = error.error?.errors?.[0].message;
        const errorMessage = `Error creating order: ${message}`;
        this.toastService.show('error', errorMessage);
      }
    })
    return req;
  }
}
